import type { Thread, ThreadEvent } from './types';

/* The sidebar badge counts threads with unread messages for my account
 * (`unreadThreadsCount`). Live events adjust it; to know whether an event
 * flips a thread between read and unread we keep what we've learned per
 * thread. When we can't tell (a thread we haven't seen yet, an event without
 * a count) the caller refetches the count instead of guessing. */

export interface ThreadUnread {
  unread: number;
  /** Latest message seq counted for this thread (replay guard). */
  seq: number;
}

export interface UnreadState {
  count: number;
  threads: Record<string, ThreadUnread>;
}

export interface UnreadStep {
  state: UnreadState;
  /** The count may be off — refetch `unreadThreadsCount`. */
  refetch: boolean;
}

export const EMPTY_UNREAD: UnreadState = { count: 0, threads: {} };

const flips = (prev: number, next: number): number =>
  (next > 0 ? 1 : 0) - (prev > 0 ? 1 : 0);

/** Move one thread to `next` unread, adjusting the count when it flips. */
export const setThreadUnread = (
  state: UnreadState,
  threadId: string,
  next: number,
  seq = 0,
): UnreadStep => {
  const known = state.threads[threadId];
  const threads = {
    ...state.threads,
    [threadId]: { unread: next, seq: Math.max(seq, known?.seq ?? 0) },
  };
  if (!known) return { state: { ...state, threads }, refetch: true };
  return {
    state: {
      count: Math.max(0, state.count + flips(known.unread, next)),
      threads,
    },
    refetch: false,
  };
};

/** MESSAGE_CREATED (INBOUND) and READ_STATE_CHANGED move the count; our own
 *  messages and delivery updates don't. */
export const applyEventToUnread = (
  state: UnreadState,
  event: ThreadEvent,
): UnreadStep => {
  if (event.kind === 'READ_STATE_CHANGED') {
    if (event.unreadCount == null) return { state, refetch: true };
    return setThreadUnread(state, event.threadId, event.unreadCount);
  }
  if (event.kind !== 'MESSAGE_CREATED' || event.message.direction !== 'INBOUND')
    return { state, refetch: false };
  const { seq } = event.message;
  const known = state.threads[event.threadId];
  if (known && seq > 0 && seq <= known.seq) return { state, refetch: false };
  const next = event.unreadCount ?? (known ? known.unread + 1 : 1);
  return setThreadUnread(state, event.threadId, Math.max(1, next), seq);
};

/** Learn the state of threads we haven't seen (from a list snapshot) without
 *  touching the count — the server's count already includes them. */
export const seedUnread = (
  state: UnreadState,
  threads: Pick<Thread, '_id' | 'unreadCount' | 'seq'>[],
): UnreadState => {
  const fresh = threads.filter((t) => !state.threads[t._id]);
  if (!fresh.length) return state;
  const next = { ...state.threads };
  fresh.forEach((t) => {
    next[t._id] = { unread: t.unreadCount ?? 0, seq: t.seq ?? 0 };
  });
  return { ...state, threads: next };
};
